import classNames from "classnames";
import { autorun, observable, runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo, useRef, useState } from "react";
// @ts-ignore
import { createRoot } from "react-dom/client";
import { CUSTOM_RENDER_INPUT_LABEL } from "../Constants";
import { ActionType, PaneOutput, PaneRenderOutput } from "../Types";
import { canvasDispatch } from "./CanvasDispatch";
import { globalValuesMobx } from "./CanvasState";
import { GraphvizRender } from "./GraphvizRender";
import { JSONRenderRoot, StringRenderOutput } from "./JSONRender";
import { TableRenderOutput } from "./TableRenderOutput";

type RenderOutputInstance = {
  render: (value: any) => void;
  unmount?: () => void;
};

function CustomRenderOutput({
  paneId,
  renderOutputCreator,
  value,
}: {
  paneId: string;
  renderOutputCreator: any;
  value: any;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);
  const [valueBox] = useState(() => observable.box(value, { deep: false }));
  useEffect(() => {
    runInAction(() => {
      valueBox.set(value);
    });
  }, [value]);
  useEffect(() => {
    try {
      if (typeof renderOutputCreator !== "function") {
        throw new Error("render input is not a function");
      }
      const instance = renderOutputCreator(rootRef.current!, {
        __EXPERIMENTAL_PANE_ID: paneId,
      });
      if (instance?.render === undefined) {
        throw new Error("render input instance must have render function");
      }
      const cleanupAutorun = autorun(() => {
        try {
          setError(undefined);
          instance.render(valueBox.get());
        } catch (e: any) {
          setError(e.toString());
        }
      });
      return () => {
        cleanupAutorun();
        try {
          instance.unmount?.();
        } catch {}
      };
    } catch (e: any) {
      setError(e.message);
    }
  }, [renderOutputCreator]);

  return (
    <>
      <div
        className={classNames("h-full", {
          hidden: error !== undefined,
        })}
        ref={rootRef}
      ></div>
      {error !== undefined ? (
        <div className="absolute top-2 left-2 text-red-400">{error}</div>
      ) : null}
    </>
  );
}

const DOMRenderOutput = (element: HTMLElement): RenderOutputInstance => ({
  render: (value: any) => {
    while (element.firstChild !== null) {
      element.removeChild(element.firstChild);
    }
    if (value instanceof Node) {
      element.appendChild(value);
    } else {
      throw new Error("value is not a DOM node");
    }
  },
});
const HTMLRenderOutput = (element: HTMLElement): RenderOutputInstance => ({
  render: (value: any) => {
    if (typeof value === "string") {
      element.innerHTML = value;
    } else {
      throw new Error("value is not a string");
    }
  },
});
export class ReactErrorBoundary extends React.Component {
  state: { error: Error | undefined } = {
    error: undefined,
  };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  render() {
    const { error } = this.state;
    if (error !== undefined) {
      const errorString = error.toString();
      return (
        <div className="text-red-400 p-2">
          <div>{errorString}</div>
          {errorString.indexOf("invariant=321") !== -1 ? (
            <div className="mt-2">
              Note: This might be fixed by connecting the right{" "}
              <span className="font-mono-natto text-primary-500">ReactDOM</span>
              input.
            </div>
          ) : null}
        </div>
      );
    }
    return this.props.children;
  }
}
const DefaultReactDOM = { createRoot };
const ReactRenderOutput = observer(
  ({
    paneId,
    value,
    reactDOMInput,
  }: {
    paneId: string;
    value: any;
    reactDOMInput: PaneOutput | undefined;
  }) => {
    const react = globalValuesMobx["React"] ?? React;
    const reactDOM =
      reactDOMInput !== undefined
        ? reactDOMInput[0] === "value"
          ? reactDOMInput[1]
          : undefined
        : globalValuesMobx["ReactDOM"] ?? DefaultReactDOM;
    const renderOutputCreatorIdRef = useRef(0);
    const renderOutputCreator = useMemo<
      (element: Element) => RenderOutputInstance
    >(() => {
      renderOutputCreatorIdRef.current++;
      return (element: Element) => {
        if (reactDOM === undefined) {
          return {
            render: () => {},
          };
        } else if (typeof reactDOM === "object" && reactDOM !== null) {
          if ("createRoot" in reactDOM) {
            // react 18
            const root = reactDOM.createRoot(element);
            return {
              render: (value: any) => {
                root.render(value);
              },
              unmount: () => {
                // This prints a console.warning about synchronously unmounting
                // during render so put in requestAnimationFrame
                requestAnimationFrame(() => {
                  root.unmount();
                });
              },
            };
          } else if ("render" in reactDOM) {
            return {
              render: (value: any) => {
                reactDOM.render(value, element);
              },
              unmount: () => {
                reactDOM.unmountComponentAtNode(element);
              },
            };
          }
        }
        return {
          render: () => {
            element.innerHTML = `<div class="font-mono-natto text-red-500 p-2">The ReactDOM input does not look like ReactDOM.</div>`;
          },
        };
      };
    }, [react, reactDOM]);
    return (
      <CustomRenderOutput
        paneId={paneId}
        renderOutputCreator={renderOutputCreator}
        value={value}
        // There's a bad transition going from react 18 to react 17 where the unmount
        // doesn't complete in the same tick so this key gives us a fresh div to
        // render into.
        key={renderOutputCreatorIdRef.current}
      />
    );
  }
);

function GraphvizRenderOutput({
  paneId,
  value,
}: {
  paneId: string;
  value: any;
}) {
  return typeof value === "string" ? (
    <GraphvizRender value={value} />
  ) : (
    <div className="p-2 text-neutral-400">
      <div>value is not a string</div>
      <div>
        <button
          onClick={() => {
            canvasDispatch([
              ActionType.SetPaneRenderOutput,
              {
                paneId,
                renderOutput: undefined,
              },
            ]);
          }}
          className="text-primary-400 hover:text-primary-500 transition"
        >
          switch to default renderer
        </button>
      </div>
    </div>
  );
}

export const PaneRenderOutputComponent = observer(
  ({
    paneId,
    extractPaneId,
    value,
    renderOutput,
    canChangeRenderOutput = false,
    disableDragExtract = false,
    getRenderInput,
  }: {
    paneId: string;
    extractPaneId?: string;
    value: any;
    renderOutput: PaneRenderOutput | undefined;
    canChangeRenderOutput?: boolean;
    disableDragExtract?: boolean;
    getRenderInput: (
      renderInputSource: [paneId: string, outputIndex: number]
    ) => PaneOutput | undefined;
  }) => {
    if (renderOutput !== undefined) {
      if (renderOutput[0] === "react") {
        let reactDOMInput: PaneOutput | undefined;
        const reactDOMInputSource = renderOutput[1]?.ReactDOM;
        if (reactDOMInputSource !== undefined) {
          reactDOMInput = getRenderInput(reactDOMInputSource);
        }
        return (
          <ReactRenderOutput
            paneId={paneId}
            value={value}
            reactDOMInput={reactDOMInput}
          />
        );
      } else if (renderOutput[0] === "dom") {
        return (
          <CustomRenderOutput
            paneId={paneId}
            renderOutputCreator={DOMRenderOutput}
            value={value}
          />
        );
      } else if (renderOutput[0] === "html") {
        return (
          <CustomRenderOutput
            paneId={paneId}
            renderOutputCreator={HTMLRenderOutput}
            value={value}
          />
        );
      } else if (renderOutput[0] === "table") {
        return (
          <TableRenderOutput
            value={value}
            paneId={paneId}
            dataPaneId={extractPaneId ?? paneId}
          />
        );
      } else if (renderOutput[0] === "text") {
        return <StringRenderOutput paneId={paneId} value={value} />;
      } else if (renderOutput[0] === "graphviz") {
        return <GraphvizRenderOutput paneId={paneId} value={value} />;
      } else if (renderOutput[0] === "custom") {
        const renderInputSource = renderOutput[1];
        if (renderInputSource !== undefined) {
          const customRenderInput = getRenderInput(renderInputSource);
          if (
            customRenderInput?.[0] === "value" &&
            typeof customRenderInput[1] === "function"
          ) {
            return (
              <CustomRenderOutput
                paneId={paneId}
                renderOutputCreator={customRenderInput[1]}
                value={value}
              />
            );
          }
        }
        return (
          <div className="p-2 text-neutral-400">
            <div className="mb-2">
              Connect a value to the{" "}
              <span className="font-mono-natto text-primary-500">
                {CUSTOM_RENDER_INPUT_LABEL}
              </span>{" "}
              input with the following type.
            </div>
            <div className="font-mono-natto whitespace-pre">{`(element: HTMLElement) => {
  render: (value: any) => void,
  unmount?: () => void,
}`}</div>
          </div>
        );
      } else {
        throw new Error(`unrecognized render output: ${renderOutput[0]}`);
      }
    } else {
      return (
        <div className="pt-2 pb-[7px] px-2">
          <JSONRenderRoot
            paneId={paneId}
            value={value}
            disableDragExtract={disableDragExtract}
            canChangeRenderOutput={canChangeRenderOutput}
          />
        </div>
      );
    }
  }
);
